import {
  Card,
  createTableColumn,
  makeStyles,
  mergeClasses,
  Table,
  TableBody,
  TableColumnDefinition,
  TableColumnId,
  TableColumnSizingOptions,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableRowData,
  tokens,
  useTableColumnSizing_unstable,
  useTableFeatures,
} from '@fluentui/react-components';
import { Segment, Transcript, isIgnoredParticipant } from '@axys-notes/common';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { MeetingTranscriptActionsBar } from './meeting-transcript-actions-bar';
import cloneDeep from 'clone-deep';
import * as uuid from 'uuid';
import { MeetingTranscriptSegmentRow } from './meeting-transcript-segment-row';
import { usePatchTranscriptMutation } from '../../queries/recording';
import { syncWordsWithTimestamps } from './sync-words';
import { AIGeneratedDisclaimer } from '../ai-generated-disclaimer';
import { RetentionDisclaimer } from '../retention-disclaimer';

type Props = {
  className?: string;
  recordingId: string;
  transcript?: Transcript;
  editable: boolean;
  transcriptExpired?: boolean;
  transcriptExpiredAt?: string;
};

type EditField = 'speaker' | 'text';

const columns: TableColumnDefinition<Segment>[] = [
  createTableColumn<Segment>({
    columnId: 'speaker',
    renderHeaderCell: () => <>Participant</>,
  }),
  createTableColumn<Segment>({
    columnId: 'segment',
    renderHeaderCell: () => <>Segment</>,
  }),
  createTableColumn<Segment>({
    columnId: 'time',
    renderHeaderCell: () => <>Timing</>,
  }),
];

const useStyles = makeStyles({
  cardStyle: {
    backgroundColor: tokens.colorSubtleBackgroundHover,
    gap: 0,
  },
  actionsBar: {
    display: 'flex',
    marginBottom: tokens.spacingVerticalL,
  },
  h2Style: {
    fontWeight: 'bold',
    paddingLeft: '15px',
    margin: `0 0 ${tokens.spacingVerticalL}`,
  },
  headerCellStyle: { fontWeight: 'bold' },
  emptyState: {
    padding: `${tokens.spacingVerticalL} 15px ${tokens.spacingVerticalL}`,
    color: tokens.colorNeutralForeground2,
    fontStyle: 'italic',
  },
});

const editKey = (field: EditField, segmentIndex: number) =>
  `${field}-${segmentIndex}`;

const formatExpiredDate = (isoDate?: string): string | undefined => {
  if (!isoDate) {
    return undefined;
  }
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const MeetingTranscript = forwardRef<HTMLDivElement, Props>(
  (props: Props, ref) => {
    const {
      transcript,
      recordingId,
      className,
      editable,
      transcriptExpired,
      transcriptExpiredAt,
    } = props;

    const [isGlobalEditing, setIsGlobalEditing] = useState(false);

    const emptyTranscript: Transcript = useMemo(
      () => ({ segments: [], roster: [] }),
      []
    );

    const [currentTranscript, setCurrentTranscript] = useState<Transcript>(
      transcript ?? emptyTranscript
    );

    const [edits, setEdits] = useState<{ [key: string]: boolean }>({});

    const speakers = useMemo(() => {
      return currentTranscript.roster
        .filter((speaker) => !isIgnoredParticipant(speaker.id))
        .reduce((acc, speaker) => {
          acc[speaker.id] = speaker.name;
          return acc;
        }, {} as { [speakerId: string]: string });
    }, [currentTranscript.roster]);

    const isEditing = useCallback(
      (field: EditField, segmentIndex: number) => {
        return edits[editKey(field, segmentIndex)] || false;
      },
      [edits]
    );

    const startEditing = useCallback(() => {
      setCurrentTranscript(cloneDeep(transcript ?? emptyTranscript));
      setIsGlobalEditing(true);
    }, [transcript, emptyTranscript]);

    const toggleUnitEdit = useCallback(
      (segmentIndex: number, field: EditField, value: boolean) => {
        setEdits({
          ...edits,
          [editKey(field, segmentIndex)]: value,
        });
      },
      [edits]
    );

    const [columnSizingOptions, setColumnSizingOptions] =
      useState<TableColumnSizingOptions>({
        speaker: {
          minWidth: 200,
          defaultWidth: 200,
        },
        segment: {
          minWidth: 200,
          defaultWidth: 1000,
        },
        time: {
          minWidth: 100,
          defaultWidth: 100,
        },
      });

    const onColumnResize = useCallback(
      (
        _: KeyboardEvent | TouchEvent | MouseEvent | undefined,
        data: {
          columnId: TableColumnId;
          width: number;
        }
      ) => {
        const { columnId, width } = data;
        setColumnSizingOptions((state) => ({
          ...state,
          [columnId]: {
            ...state[columnId],
            idealWidth: width,
          },
        }));
      },
      []
    );

    const { tableRef, columnSizing_unstable, getRows } =
      useTableFeatures<Segment>(
        { columns, items: currentTranscript.segments },
        [useTableColumnSizing_unstable({ columnSizingOptions, onColumnResize })]
      );

    const rows: TableRowData<Segment>[] = getRows();

    const onSaveSpeakerEdit = useCallback(
      (
        segmentIndex: number,
        speakerId: string | undefined,
        speakerName: string
      ) => {
        toggleUnitEdit(segmentIndex, 'speaker', false);
        let id = speakerId || '';
        if (!speakerId) {
          id = `unknown-${uuid.v4()}`;
          currentTranscript.roster.push({
            id,
            name: speakerName,
            organizer: false,
          });
        }
        currentTranscript.segments[segmentIndex].speaker = id;
        setCurrentTranscript({
          segments: [...currentTranscript.segments],
          roster: [...currentTranscript.roster],
        });
      },
      [toggleUnitEdit, currentTranscript]
    );

    const onCancelSpeakerEdit = useCallback(
      (segmentIndex: number) => {
        toggleUnitEdit(segmentIndex, 'speaker', false);
      },
      [toggleUnitEdit]
    );

    const onStartSpeakerEdit = useCallback(
      (segmentIndex: number) => {
        toggleUnitEdit(segmentIndex, 'speaker', true);
      },
      [toggleUnitEdit]
    );

    const onSaveTextEdit = useCallback(
      (segmentIndex: number, value: string) => {
        toggleUnitEdit(segmentIndex, 'text', false);
        const segment = currentTranscript.segments[segmentIndex];

        if (segment) {
          // Mettre à jour le texte
          segment.text = value;

          // Synchroniser les mots avec leurs timestamps
          segment.words = syncWordsWithTimestamps(
            segment.words,
            value,
            segment.start,
            segment.end
          );
        }

        setCurrentTranscript({
          segments: [...currentTranscript.segments],
          roster: [...currentTranscript.roster],
        });
      },
      [toggleUnitEdit, currentTranscript]
    );

    const onCancelTextEdit = useCallback(
      (segmentIndex: number) => {
        toggleUnitEdit(segmentIndex, 'text', false);
      },
      [toggleUnitEdit]
    );

    const onStartTextEdit = useCallback(
      (segmentIndex: number) => {
        toggleUnitEdit(segmentIndex, 'text', true);
      },
      [toggleUnitEdit]
    );

    const hasAnyPendingEdit = useMemo(
      () => Object.values(edits).some((value) => value),
      [edits]
    );

    const globalCancel = useCallback(() => {
      setEdits({});
      setCurrentTranscript(transcript ?? emptyTranscript);
      setIsGlobalEditing(false);
    }, [transcript, emptyTranscript]);

    const patchTranscriptMutation = usePatchTranscriptMutation();

    const globalSave = useCallback(() => {
      if (!transcript) {
        return;
      }
      patchTranscriptMutation.mutate(
        {
          recordingId,
          originalTranscript: transcript,
          modifiedTranscript: currentTranscript,
        },
        {
          onSuccess: () => {
            setEdits({});
            setIsGlobalEditing(false);
          },
        }
      );
    }, [recordingId, transcript, currentTranscript, patchTranscriptMutation]);

    const styles = useStyles();

    return (
      <Card
        ref={ref}
        className={
          className
            ? mergeClasses(styles.cardStyle, className)
            : styles.cardStyle
        }
      >
        <div className={styles.actionsBar}>
          <MeetingTranscriptActionsBar
            recordingId={recordingId}
            startEditing={startEditing}
            isEditing={isGlobalEditing}
            cancelEditing={globalCancel}
            saveEditing={globalSave}
            hasAnyPendingEdit={hasAnyPendingEdit}
            editable={editable}
            transcriptExpired={transcriptExpired}
          />
        </div>
        <div>
          <h2 className={styles.h2Style}>Transcription de la réunion</h2>
          <RetentionDisclaimer />
          {transcriptExpired ? (
            <div className={styles.emptyState}>
              {(() => {
                const date = formatExpiredDate(transcriptExpiredAt);
                return date
                  ? `Transcription supprimée le ${date} conformément à notre politique de conservation. Le compte-rendu reste disponible.`
                  : 'Transcription supprimée conformément à notre politique de conservation. Le compte-rendu reste disponible.';
              })()}
            </div>
          ) : (
            <>
              <AIGeneratedDisclaimer />
              <div style={{ padding: '0 10px' }}>
                <Table ref={tableRef} {...columnSizing_unstable.getTableProps()}>
                  <TableHeader>
                    <TableRow>
                      {columns.map((column) => (
                        <TableHeaderCell
                          key={column.columnId}
                          className={styles.headerCellStyle}
                          {...columnSizing_unstable.getTableHeaderCellProps(
                            column.columnId
                          )}
                        >
                          {column.renderHeaderCell()}
                        </TableHeaderCell>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((item, index) => {
                      const previousSpeaker =
                        index > 0 ? rows[index - 1] : undefined;
                      const sameSpeaker =
                        previousSpeaker?.item?.speaker === item.item.speaker;
                      return (
                        <MeetingTranscriptSegmentRow
                          key={item.rowId}
                          isEditable={isGlobalEditing}
                          speakers={speakers}
                          sameSpeaker={sameSpeaker}
                          segment={item.item}
                          segmentIndex={index}
                          columnSizing={columnSizing_unstable}
                          isEditingSpeaker={isEditing('speaker', index)}
                          onCancelSpeakerEdit={onCancelSpeakerEdit}
                          onSaveSpeakerEdit={onSaveSpeakerEdit}
                          onStartSpeakerEdit={onStartSpeakerEdit}
                          isEditingText={isEditing('text', index)}
                          onCancelTextEdit={onCancelTextEdit}
                          onSaveTextEdit={onSaveTextEdit}
                          onStartTextEdit={onStartTextEdit}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      </Card>
    );
  }
);
